import { Dispatch, SetStateAction, useState } from 'react';
import { QueryObserverResult } from 'react-query';

import { OutboundIcon } from '../../../../assets/svg/navigation/outbound';
import { DateAndTime } from '../../../../components/date-and-time';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { ETPayTable } from '../../../../components/table';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Beneficiary } from '../../../../models/payouts';
import {
	ColumnInterface,
	Filters,
	FilterStatus,
	Pagination,
	RowInterface,
} from '../../../../models/table';
import { PayoutsService } from '../../../../services/payouts';
import { validatePermission } from '../../../../utils/common';
import { errorFormatter } from '../../../../utils/errors';
import DropDown from '../dropdown';
import EditModal from '../editModal';
import ModalPayouts from '../modal';
import RemoveModal from '../removeModal';
import {
	CellContainer,
	Content,
	DetailsContainer,
	Download,
	ExportModalContent,
	ExportModalDescription,
	ExportModalTitle,
	Section,
	Title,
	TransferButton,
	Value,
} from './styles';

interface RefundsTableProps {
	data: Beneficiary[];
	isLoading?: boolean;
	filters: Filters;
	onFilterChange: Dispatch<SetStateAction<Filters>>;
	pagination: Pagination;
	today?: Date;
	pastDate?: Date;
	setPagination: Dispatch<SetStateAction<Pagination>>;
	totalResults?: number;
	stateList: FilterStatus[];
	refetch: () => Promise<QueryObserverResult<any, unknown>>;
}

interface ModalProps {
	loader: boolean;
	show: boolean;
	url: string;
}

const defaultModal: ModalProps = {
	loader: false,
	show: false,
	url: '',
};

interface Bank {
	name: string;
	code: string;
}

export interface Values {
	account: string;
	amount: string;
	bank: Bank;
	code: string;
	codeOtp: string;
	email: string;
	loading: boolean;
	message: string;
	name: string;
	reference: string;
	requestDate: string;
	rut: string;
	token: string;
	error: boolean;
	errorMessage: string;
	retryFunction?: () => void;
}

export const defaultValues: Values = {
	account: '',
	amount: '',
	bank: { name: '', code: '' },
	code: '',
	codeOtp: '',
	email: '',
	loading: false,
	message: '',
	name: '',
	reference: '',
	requestDate: '',
	rut: '',
	token: '',
	error: false,
	errorMessage: '',
};

const BeneficiaryTable = ({
	data,
	isLoading,
	onFilterChange,
	pagination,
	setPagination,
	totalResults,
	refetch,
}: RefundsTableProps) => {
	const { merchantSelected } = useMerchant();
	const [modal, setModal] = useState<ModalProps>(defaultModal);
	const { handleShowAlert } = useFloatingAlert();
	const [modalPayouts, setModalPayouts] = useState<boolean>(false);
	const [editModal, setEditModal] = useState<boolean>(false);
	const [removeModal, setRemoveModal] = useState<boolean>(false);
	const [state, setState] = useState<Values>(defaultValues);
	const [selectedRowData, setSelectedRowData] = useState<Beneficiary | undefined>(undefined);

	const handleExportAction = async () => {
		setModal({ ...modal, loader: true, show: true });
		try {
			const response = await PayoutsService.getExportDataBeneficiary({
				merchantCode: merchantSelected?.code as string,
				page: pagination,
			});

			const url = window.URL.createObjectURL(new Blob([response.csv]));
			if (response.csv) setModal({ loader: false, show: true, url: url });
		} catch (e: any) {
			handleShowAlert({
				type: 'error',
				message: errorFormatter(e.code),
			});
		}
	};

	const onClose = () => {
		setModalPayouts(false);
		setState(defaultValues);
		refetch();
	};

	const onCloseEdit = () => {
		setEditModal(false);
		setState(defaultValues);
		refetch();
	};

	const onCloseFinalize = () => {
		setRemoveModal(false);
		setState(defaultValues);
		refetch();
	};

	const dropdownOptions = [
		{
			label: 'Editar',
			onClick: (rowData: Beneficiary) => {
				setSelectedRowData(rowData);
				setEditModal(true);
			},
		},
		{
			label: 'Eliminar',
			onClick: (rowData: Beneficiary) => {
				setSelectedRowData(rowData);
				setRemoveModal(true);
			},
			colorCustom: '#FF433E ',
		},
	];

	const columns: ColumnInterface<Beneficiary>[] = [
		{
			sortField: true,
			title: 'Nombre',
			propertyName: 'name',
			cellRender: ({ name }) => <>{name}</>,
		},
		{
			sortField: true,
			title: 'Rut',
			propertyName: 'rut',
			cellRender: ({ rut }) => <>{rut}</>,
		},
		{
			title: 'Banco',
			propertyName: 'bankName',
			cellRender: ({ bankName }) => <>{bankName}</>,
		},
		{
			sortField: true,
			title: 'N°Cuenta',
			propertyName: 'account',
			cellRender: ({ account }) => <>{account && account.replace(/\d(?=\d{4})/g, '*')}</>,
		},
		{
			sortField: true,
			title: 'Añadido el',
			propertyName: 'created_at',
			cellRender: ({ created_at }) => {
				const dateObject = new Date(created_at);
				const day = String(dateObject.getDate()).padStart(2, '0');
				const month = String(dateObject.getMonth() + 1).padStart(2, '0');
				const year = dateObject.getFullYear();
				const dateCreated = `${day}/${month}/${year}`;
				const timeCreated = new Date(created_at).toLocaleTimeString('es-ES', {
					hour: 'numeric',
					minute: 'numeric',
				});
				return <DateAndTime date={dateCreated} time={timeCreated} />;
			},
		},
		{
			align: 'center',
			title: '',
			propertyName: 'transfer',
			cellRender: (rowData) => (
				<>
					<CellContainer>
						<TransferButton
							onClick={() => {
								setModalPayouts(true);
								setState({
									...state,
									rut: rowData.rut,
									name: rowData.name,
									email: rowData.email,
									account: rowData.account,
									bank: {
										name: rowData.bankName,
										code: rowData.bankSbifNumber,
									},
								});
							}}
						>
							<OutboundIcon />
						</TransferButton>
						<DropDown options={dropdownOptions} rowData={rowData} />
					</CellContainer>
				</>
			),
		},
	];

	const detailSection: RowInterface<Beneficiary> = (data) => {
		return (
			<DetailsContainer>
				<Section>
					<Content>
						<Title>Nombre del beneficiario</Title>
						<Value>{data.name}</Value>
					</Content>
					<Content>
						<Title>Correo electrónico</Title>
						<Value>{data.email}</Value>
					</Content>
				</Section>
			</DetailsContainer>
		);
	};

	const handleCloseModal = () => {
		setModal(defaultModal);
	};

	return (
		<>
			{modal.show && (
				<Modal
					title="Exportar"
					show={modal.show}
					onClose={handleCloseModal}
					allowCloseModal={!modal.loader}
				>
					{modal.loader ? (
						<Spinner />
					) : (
						<ExportModalContent>
							<ExportModalTitle>Tu reporte está listo</ExportModalTitle>

							<ExportModalDescription>
								Hemos terminado de recopilar los datos de tu reporte. Haz clic en el botón para
								descargarlo.
							</ExportModalDescription>
							<Download href={modal.url} download="beneficiarios.csv">
								Descargar reporte
							</Download>
						</ExportModalContent>
					)}
				</Modal>
			)}
			{editModal && (
				<EditModal
					beneficiary={selectedRowData || ({} as Beneficiary)}
					state={state}
					setState={setState}
					onClose={onCloseEdit}
				/>
			)}

			{removeModal && (
				<RemoveModal
					beneficiary={selectedRowData || ({} as Beneficiary)}
					state={state}
					setState={setState}
					onClose={onCloseFinalize}
				/>
			)}

			{modalPayouts && <ModalPayouts onClose={onClose} state={state} setState={setState} />}

			<ETPayTable<Beneficiary>
				title="Beneficiarios"
				data={data}
				isLoading={!!isLoading}
				columns={columns}
				row={detailSection}
				allowExport={validatePermission('PAYOUTS_EXPORTS')}
				exportAction={handleExportAction}
				onFilterChange={onFilterChange}
				hasFilters
				hasSearch
				hasPagination
				totalResults={totalResults}
				pagination={pagination}
				emptyMessage="No hay información disponible en el periodo seleccionado"
				setPagination={setPagination}
			/>
		</>
	);
};

export { BeneficiaryTable };
